import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  CircleHelp,
  Clock3,
  CloudOff,
  CreditCard,
  LifeBuoy,
  Loader2,
  MessageCircle,
  PackageSearch,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  Store,
} from "lucide-react";
import { toast } from "sonner";

import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLocalization } from "@/lib/localization";
import { supportCopy } from "@/lib/supportLocalization";
import { useAuth, useWorkspace } from "@/lib/store";
import {
  createSupportTicket,
  fetchSupportCategories,
  fetchSupportTicket,
  fetchSupportTickets,
  replySupportTicket,
} from "@/lib/shopApi";
import { formatRelative } from "@/lib/formatters";

const CATEGORY_META = {
  sync: { icon: CloudOff, tint: "bg-sky-50 text-sky-700", glow: "from-sky-400/25" },
  payments: { icon: CreditCard, tint: "bg-amber-50 text-amber-700", glow: "from-amber-400/25" },
  access: { icon: ShieldCheck, tint: "bg-violet-50 text-violet-700", glow: "from-violet-400/25" },
  inventory: { icon: PackageSearch, tint: "bg-emerald-50 text-emerald-700", glow: "from-emerald-400/25" },
  other: { icon: CircleHelp, tint: "bg-slate-100 text-slate-700", glow: "from-slate-400/20" },
};

function statusTone(status) {
  if (status === "waiting_on_customer") return "border-amber-200 bg-amber-50 text-amber-800";
  if (status === "resolved" || status === "closed") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  return "border-sky-200 bg-sky-50 text-sky-800";
}

function localizedCategory(copy, category) {
  const localized = copy.categories?.[category?.slug];
  return localized ? { ...category, description: localized[1], name: localized[0] } : category;
}

function slaCopy(copy, ticket) {
  if (ticket.firstRespondedAt) return copy.responseJoined;
  const due = new Date(ticket.firstResponseDueAt).getTime();
  const minutes = Math.max(0, Math.round((due - Date.now()) / 60000));
  if (minutes <= 0) return copy.responseNow;
  if (minutes < 60) return copy.responseMinutes(minutes);
  return copy.responseHours(Math.ceil(minutes / 60));
}

export default function Support() {
  const { language, locale, messages } = useLocalization();
  const copy = supportCopy[language] || supportCopy.en;
  const { session } = useAuth();
  const { currentBranch, sync } = useWorkspace();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [reply, setReply] = useState("");
  const sessionId = session?.sessionId;

  const ticketsQuery = useQuery({
    enabled: Boolean(sessionId),
    queryKey: ["support-tickets", sessionId],
    queryFn: () => fetchSupportTickets(sessionId),
    refetchInterval: 45_000,
  });
  const categoriesQuery = useQuery({
    enabled: Boolean(sessionId),
    queryKey: ["support-categories"],
    queryFn: () => fetchSupportCategories(sessionId),
    staleTime: 10 * 60_000,
  });
  const detailQuery = useQuery({
    enabled: Boolean(sessionId && selectedId),
    queryKey: ["support-ticket", selectedId],
    queryFn: () => fetchSupportTicket(sessionId, selectedId),
    refetchInterval: 30_000,
  });
  const replyMutation = useMutation({
    mutationFn: () => replySupportTicket(sessionId, selectedId, reply.trim()),
    onSuccess: async (payload) => {
      setReply("");
      queryClient.setQueryData(["support-ticket", selectedId], payload);
      await queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      toast.success(copy.replySent);
    },
    onError: (error) => toast.error(error.message || copy.replyFailed),
  });

  const tickets = ticketsQuery.data?.items || [];
  const active = tickets.filter((ticket) => !["resolved", "closed"].includes(ticket.status));
  const waiting = active.find((ticket) => ticket.status === "waiting_on_customer");
  const deskState = ticketsQuery.isLoading
    ? { dot: "bg-slate-400", label: copy.checking, tone: "bg-slate-100 text-slate-600" }
    : ticketsQuery.isError
      ? { dot: "bg-rose-400", label: copy.unavailable, tone: "bg-rose-50 text-rose-700" }
      : waiting
        ? { dot: "bg-amber-400", label: copy.replyNeeded, tone: "bg-amber-50 text-amber-700" }
        : { dot: "bg-emerald-500", label: copy.allCaughtUp, tone: "bg-emerald-50 text-emerald-700" };

  return (
    <PageContainer className="max-w-[1360px]">
      <section className="flex flex-col gap-5 border-b border-border/70 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/[0.08] text-primary ring-1 ring-primary/10">
            <LifeBuoy className="h-[18px] w-[18px]" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold tracking-[-0.035em]">{messages.layout.support}</h2>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-semibold ${deskState.tone}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${deskState.dot}`} />
                {deskState.label}
              </span>
            </div>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              {messages.layout.supportSubtitle}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          {waiting ? (
            <Button
              variant="outline"
              onClick={() => setSelectedId(waiting.id)}
              className="h-10 rounded-xl border-amber-200 bg-amber-50 px-4 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
            >
              <MessageCircle className="mr-2 h-4 w-4" /> {copy.reply}
            </Button>
          ) : null}
          <Button
            data-testid="support-new-case"
            onClick={() => setCreateOpen(true)}
            className="h-10 rounded-xl px-4 shadow-sm"
          >
            <Plus className="mr-2 h-4 w-4" /> {copy.newCase}
          </Button>
        </div>
      </section>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
        <section>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">{copy.cases}</h3>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{tickets.length}</span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{copy.casesHint}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" aria-label={copy.refresh} onClick={() => ticketsQuery.refetch()} disabled={ticketsQuery.isFetching}>
              <RefreshCw className={`h-3.5 w-3.5 ${ticketsQuery.isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {ticketsQuery.isLoading ? (
            <div className="rounded-2xl border bg-card p-12 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin" /> {copy.loading}
            </div>
          ) : ticketsQuery.isError ? (
            <div className="rounded-2xl border bg-card p-8 text-center">
              <div className="text-sm font-medium">{copy.loadFailed}</div>
              <p className="mt-1 text-xs text-muted-foreground">{copy.loadSafe}</p>
              <Button variant="outline" size="sm" className="mt-4 rounded-full" onClick={() => ticketsQuery.refetch()}>{messages.assistantPage.tryAgain}</Button>
            </div>
          ) : tickets.length === 0 ? (
            <div className="rounded-2xl border bg-card px-6 py-14 text-center shadow-sm">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/[0.07] text-primary">
                <MessageCircle className="h-5 w-5" />
              </div>
              <h4 className="mt-4 text-sm font-semibold">{copy.empty}</h4>
              <p className="mx-auto mt-1 max-w-xs text-xs leading-5 text-muted-foreground">
                {copy.emptyHint}
              </p>
              <Button variant="outline" size="sm" className="mt-4 rounded-full px-4" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-3.5 w-3.5" /> {copy.newCase}
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border bg-card shadow-[0_10px_35px_-28px_rgba(15,70,68,0.45)]">
              {tickets.map((ticket, index) => (
                <button
                  key={ticket.id}
                  data-testid={`support-ticket-${ticket.id}`}
                  onClick={() => setSelectedId(ticket.id)}
                  className={`group flex w-full items-center gap-3.5 px-4 py-3.5 text-left transition hover:bg-muted/35 sm:px-5 ${
                    index ? "border-t" : ""
                  }`}
                >
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${CATEGORY_META[ticket.category.slug]?.tint || CATEGORY_META.other.tint}`}>
                    {React.createElement(CATEGORY_META[ticket.category.slug]?.icon || CircleHelp, { className: "h-4 w-4" })}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{ticket.subject}</span>
                      {ticket.status === "waiting_on_customer" && <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground/65">{ticket.publicReference}</span>
                      <span>·</span><span>{localizedCategory(copy, ticket.category).name}</span><span>·</span><span>{formatRelative(ticket.updatedAt, locale)}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className={`hidden rounded-full text-[10px] sm:inline-flex ${statusTone(ticket.status)}`}>
                    {copy.status[ticket.status] || ticket.status}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </button>
              ))}
            </div>
          )}
        </section>

        <aside>
          <div className="rounded-2xl border bg-card p-4 shadow-[0_10px_35px_-30px_rgba(15,70,68,0.5)]">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <Store className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold">{copy.context}</div>
                <div className="text-xs text-muted-foreground">{currentBranch?.name || copy.currentBranch}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <ContextPill label={copy.connection} value={sync?.online ? copy.online : copy.offline} />
              <ContextPill label={copy.syncQueue} value={copy.waiting(sync?.queuedCount || 0)} />
            </div>
            <div className="mt-4 flex gap-2.5 border-t pt-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p className="text-[11px] leading-5 text-muted-foreground">
                {copy.safeContext}
              </p>
            </div>
          </div>
        </aside>
      </div>

      <CreateCaseDialog
        copy={copy}
        categories={categoriesQuery.data?.categories || []}
        categoriesError={categoriesQuery.isError}
        categoriesLoading={categoriesQuery.isLoading || categoriesQuery.isFetching}
        open={createOpen}
        onOpenChange={setCreateOpen}
        sessionId={sessionId}
        currentBranch={currentBranch}
        sync={sync}
        retryLabel={messages.assistantPage.tryAgain}
        onRetryCategories={() => categoriesQuery.refetch()}
        onCreated={async (ticket) => {
          await queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
          setCreateOpen(false);
          setSelectedId(ticket.id);
        }}
      />

      <Dialog open={Boolean(selectedId)} onOpenChange={(open) => !open && setSelectedId(null)}>
        <DialogContent className="flex max-h-[88vh] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
          {detailQuery.isLoading ? (
            <div className="p-12 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin" /> {copy.opening}</div>
          ) : detailQuery.data?.ticket ? (
            <TicketConversation
              locale={locale}
              copy={copy}
              ticket={detailQuery.data.ticket}
              reply={reply}
              setReply={setReply}
              sending={replyMutation.isPending}
              onSend={() => reply.trim() && replyMutation.mutate()}
            />
          ) : (
            <div className="p-10 text-center">
              <p className="text-sm text-muted-foreground">{copy.openFailed}</p>
              <Button variant="outline" className="mt-4" onClick={() => detailQuery.refetch()}>{messages.assistantPage.tryAgain}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function ContextPill({ label, value }) {
  return (
    <div className="rounded-xl bg-muted/55 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xs font-semibold">{value}</div>
    </div>
  );
}

function CreateCaseDialog({ categories, categoriesError, categoriesLoading, copy, currentBranch, onCreated, onOpenChange, onRetryCategories, open, retryLabel, sessionId, sync }) {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [impact, setImpact] = useState("continue");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [includeContext, setIncludeContext] = useState(true);
  const mutation = useMutation({
    mutationFn: () => createSupportTicket(sessionId, {
      body: body.trim(),
      category,
      context: includeContext ? {
        branchName: currentBranch?.name || "",
        online: Boolean(sync?.online),
        queuedSyncOperations: sync?.queuedCount || 0,
        failedSyncOperations: sync?.failedCount || 0,
        surface: "merchant-web",
      } : {},
      impact,
      source: "web",
      subject: subject.trim(),
    }),
    onSuccess: (payload) => {
      toast.success(`${payload.ticket.publicReference} · ${copy.status.waiting_on_support}`);
      onCreated(payload.ticket);
    },
    onError: (error) => toast.error(error.message || copy.createFailed),
  });

  useEffect(() => {
    if (!open) {
      setStep(1); setCategory(""); setImpact("continue"); setSubject(""); setBody(""); setIncludeContext(true);
    }
  }, [open]);

  const selectedCategory = localizedCategory(copy, categories.find((item) => item.slug === category));
  const impactItems = ['continue', 'serious', 'blocked'].map((value, index) => ({
    body: copy.impacts[index][1], title: copy.impacts[index][0], value,
  }));
  const canContinue = step === 1 ? Boolean(category) : subject.trim().length >= 4 && body.trim().length >= 4;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl p-0">
        <div className="border-b bg-[linear-gradient(135deg,hsl(var(--primary)/.09),transparent)] px-6 py-5">
          <DialogHeader>
            <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">{step}</span>
              {step} / 2
            </div>
            <DialogTitle className="text-2xl tracking-[-0.03em]">{step === 1 ? copy.whatHelp : copy.tellUs}</DialogTitle>
            <DialogDescription>{step === 1 ? copy.chooseLane : `${selectedCategory?.name || copy.support} · ${impactItems.find((item) => item.value === impact)?.title}`}</DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6">
          {step === 1 ? (
            categoriesLoading ? (
              <div className="py-12 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />{copy.loading}</div>
            ) : categoriesError || categories.length === 0 ? (
              <div className="rounded-2xl border bg-muted/20 px-6 py-10 text-center">
                <CircleHelp className="mx-auto h-6 w-6 text-muted-foreground" />
                <div className="mt-3 text-sm font-semibold">{copy.loadFailed}</div>
                <p className="mt-1 text-xs text-muted-foreground">{copy.loadSafe}</p>
                <Button type="button" size="sm" variant="outline" className="mt-4 rounded-full" onClick={onRetryCategories}><RefreshCw className="mr-2 h-3.5 w-3.5" />{retryLabel}</Button>
              </div>
            ) : <div className="grid gap-3 sm:grid-cols-2">
              {categories.map((item) => {
                const localizedItem = localizedCategory(copy, item);
                const meta = CATEGORY_META[item.slug] || CATEGORY_META.other;
                const Icon = meta.icon;
                const selected = item.slug === category;
                return (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => setCategory(item.slug)}
                    className={`relative overflow-hidden rounded-2xl border p-4 text-left transition-all ${
                      selected ? "border-primary bg-primary/[0.045] ring-2 ring-primary/10" : "hover:border-primary/30 hover:bg-muted/30"
                    }`}
                  >
                    <div className={`absolute inset-x-0 top-0 h-16 bg-gradient-to-b ${meta.glow} to-transparent opacity-60`} />
                    <div className="relative flex items-start gap-3">
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${meta.tint}`}><Icon className="h-5 w-5" /></span>
                      <span>
                        <span className="block text-sm font-semibold">{localizedItem.name}</span>
                        <span className="mt-1 block text-xs leading-5 text-muted-foreground">{localizedItem.description}</span>
                      </span>
                    </div>
                    {selected && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"><Check className="h-3 w-3" /></span>}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{copy.impactQuestion}</label>
                <div className="mt-2 grid gap-2">
                  {impactItems.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setImpact(item.value)}
                      className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition ${
                        impact === item.value ? "border-primary bg-primary/[0.045]" : "hover:bg-muted/35"
                      }`}
                    >
                      <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-4 ${impact === item.value ? "border-primary bg-white" : "border-muted bg-muted"}`} />
                      <span><span className="block text-sm font-medium">{item.title}</span><span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{item.body}</span></span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="support-subject" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{copy.shortSummary}</label>
                <Input id="support-subject" data-testid="support-subject" value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={160} className="mt-2 h-11 rounded-xl" placeholder={copy.subjectPlaceholder} />
              </div>
              <div>
                <label htmlFor="support-body" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{copy.body}</label>
                <Textarea id="support-body" data-testid="support-body" value={body} onChange={(event) => setBody(event.target.value)} maxLength={10000} rows={5} className="mt-2 resize-none rounded-xl" placeholder={copy.bodyPlaceholder} />
              </div>
              <button type="button" onClick={() => setIncludeContext((value) => !value)} className="flex w-full items-start gap-3 rounded-xl border bg-muted/25 p-3.5 text-left">
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${includeContext ? "border-primary bg-primary text-primary-foreground" : ""}`}>
                  {includeContext && <Check className="h-3 w-3" />}
                </span>
                <span><span className="block text-sm font-medium">{copy.includeContext}</span><span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{copy.contextBody}</span></span>
              </button>
            </div>
          )}
          <div className="mt-6 flex items-center justify-between border-t pt-5">
            {step === 2 ? (
              <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="mr-2 h-4 w-4" /> {copy.back}</Button>
            ) : <span />}
            <Button
              data-testid="support-continue"
              disabled={!canContinue || mutation.isPending}
              onClick={() => step === 1 ? setStep(2) : mutation.mutate()}
              className="h-11 rounded-xl px-5"
            >
              {mutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {copy.sending}</> : step === 1 ? <>{copy.continue} <ArrowRight className="ml-2 h-4 w-4" /></> : <>{copy.send} <Send className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TicketConversation({ copy, locale, onSend, reply, sending, setReply, ticket }) {
  return (
    <>
      <div className="border-b bg-muted/20 px-6 py-5">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pr-8">
            <Badge variant="outline" className={`rounded-full ${statusTone(ticket.status)}`}>{copy.status[ticket.status] || ticket.status}</Badge>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{ticket.publicReference}</span>
          </div>
          <DialogTitle className="pt-2 text-xl tracking-[-0.025em]">{ticket.subject}</DialogTitle>
          <DialogDescription>{localizedCategory(copy, ticket.category).name} · {ticket.branchName || copy.shopWide}</DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-primary/10 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
          <Clock3 className="h-3.5 w-3.5 text-primary" /> {slaCopy(copy, ticket)}
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-[radial-gradient(circle_at_top,hsl(var(--primary)/.035),transparent_45%)] px-5 py-5 sm:px-6">
        {(ticket.messages || []).map((message) => {
          const merchant = message.authorType === "merchant";
          return (
            <div key={message.id} className={`flex ${merchant ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[86%] rounded-2xl px-4 py-3 ${
                merchant ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md border bg-card shadow-sm"
              }`}>
                <div className={`text-[10px] font-semibold uppercase tracking-wider ${merchant ? "text-primary-foreground/65" : "text-muted-foreground"}`}>
                  {merchant ? copy.you : message.authorName} · {formatRelative(message.createdAt, locale)}
                </div>
                <div className="mt-1.5 whitespace-pre-wrap text-sm leading-6">{message.body}</div>
              </div>
            </div>
          );
        })}
      </div>
      {!["closed"].includes(ticket.status) && (
        <div className="border-t bg-background p-4 sm:p-5">
          <div className="flex items-end gap-3">
            <Textarea
              data-testid="support-reply"
              value={reply}
              onChange={(event) => setReply(event.target.value)}
              rows={2}
              className="max-h-32 min-h-[48px] resize-none rounded-xl"
              placeholder={ticket.status === "resolved" ? copy.reopenReply : copy.writeReply}
            />
            <Button data-testid="support-send-reply" size="icon" onClick={onSend} disabled={!reply.trim() || sending} className="h-12 w-12 shrink-0 rounded-xl">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
